import { RefObject, useImperativeHandle, useState } from 'react';
import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import {
  IconFileTypePdf,
  IconPhoto,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import type { EtcTicketSummary } from '#/components/ui/tickets/etc/model';
import { TICKET_TYPE_CONFIG } from '../../../config';
import type {
  ConfirmPaymentOnBehalfDialogRef,
  ConfirmPaymentOnBehalfResult,
} from './useConfirmPaymentOnBehalfDialog';

const RECEIPT_ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png];
const RECEIPT_MAX_SIZE_MB = 10;
const RECEIPT_MAX_SIZE = RECEIPT_MAX_SIZE_MB * 1024 ** 2;
const RECEIPT_RULE_TEXT = `PDF、JPG、PNG，檔案大小上限 ${RECEIPT_MAX_SIZE_MB} MB`;

const formatFileSize = (bytes: number) =>
  bytes < 1024 ** 2
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / 1024 ** 2).toFixed(1)} MB`;

export function ConfirmPaymentOnBehalfDialog({
  modalRef,
}: {
  modalRef: RefObject<ConfirmPaymentOnBehalfDialogRef | null>;
}) {
  const [state, setState] = useState<
    | {
        ticket: EtcTicketSummary;
        onConfirm: (file: File | null) => void;
        onCancel: () => void;
      }
    | undefined
  >();
  const [file, setFile] = useState<File | null>(null);
  const [rejectMessage, setRejectMessage] = useState<string | null>(null);

  const isOpen = !!state;

  useImperativeHandle(modalRef, () => ({
    confirm: async (ticket: EtcTicketSummary) => {
      const result = await new Promise<ConfirmPaymentOnBehalfResult | null>(
        (resolve) => {
          setState({
            ticket,
            onConfirm: (file) => resolve({ file }),
            onCancel: () => resolve(null),
          });
        },
      );

      setState(undefined);
      setFile(null);
      setRejectMessage(null);
      return result;
    },
  }));

  const ticket = state?.ticket;

  if (!ticket) {
    return null;
  }

  const handleCancel = state.onCancel;
  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Stack gap={2}>
          <Text fw={600} size="lg">
            確認已代繳
          </Text>
          <Text size="sm" c="dimmed">
            {`${ticket.account.email}．${TICKET_TYPE_CONFIG['etc-tickets'].name}．${ticket.plateNumber}．${ticket.idNumber}`}
          </Text>
        </Stack>
      }
      size="lg"
    >
      <Stack gap="md">
        <Text size="md" fw={500}>
          {`請確認已透過${TICKET_TYPE_CONFIG['etc-tickets'].queryPlatform}
            完成線下代繳，提交後此票將標記為「繳款成功」並結案，系統將寄送繳款成功通知。`}
        </Text>
        <Flex
          bg={'rgb(248, 249, 250)'}
          bdrs="lg"
          p="lg"
          direction="column"
          gap="sm"
        >
          <Text size="sm" fw={500} c="dimmed">
            線上代繳金額
          </Text>
          <Text size="30px" fw={700}>
            {`NT$ ${ticket.amount}`}
          </Text>
        </Flex>

        <Divider />

        <Stack gap={6}>
          <Text size="sm" fw={500}>
            交易明細
          </Text>

          {file ? (
            <Paper withBorder radius="md" p="sm">
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" miw={0}>
                  <ThemeIcon variant="light" size="lg" radius="md">
                    {file.type === MIME_TYPES.pdf ? (
                      <IconFileTypePdf size={20} />
                    ) : (
                      <IconPhoto size={20} />
                    )}
                  </ThemeIcon>
                  <Stack gap={0} miw={0}>
                    <Text size="sm" fw={500} truncate>
                      {file.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatFileSize(file.size)}
                    </Text>
                  </Stack>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="移除檔案"
                  onClick={() => setFile(null)}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Paper>
          ) : (
            <Dropzone
              accept={RECEIPT_ACCEPT}
              maxSize={RECEIPT_MAX_SIZE}
              maxFiles={1}
              multiple={false}
              radius="md"
              onDrop={([dropped]) => {
                setFile(dropped ?? null);
                setRejectMessage(null);
              }}
              onReject={() => setRejectMessage(`僅接受 ${RECEIPT_RULE_TEXT}`)}
            >
              <Stack
                align="center"
                gap={6}
                py="lg"
                style={{ pointerEvents: 'none' }}
              >
                <Dropzone.Accept>
                  <IconUpload
                    size={36}
                    stroke={1.5}
                    color="var(--mantine-color-blue-6)"
                  />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX
                    size={36}
                    stroke={1.5}
                    color="var(--mantine-color-red-6)"
                  />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload
                    size={36}
                    stroke={1.5}
                    color="var(--mantine-color-dimmed)"
                  />
                </Dropzone.Idle>
                <Text size="sm" fw={500}>
                  拖曳檔案到這裡，或點擊選擇檔案
                </Text>
                <Text size="xs" c="dimmed">
                  支援 {RECEIPT_RULE_TEXT}
                </Text>
              </Stack>
            </Dropzone>
          )}

          {rejectMessage && (
            <Text size="xs" c="red">
              {rejectMessage}
            </Text>
          )}
        </Stack>

        {/* Footer */}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleCancel}>
            取消
          </Button>
          <Button onClick={() => state.onConfirm(file)}>確認已代繳</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
